
import { supabase, tables } from "@/lib/supabase";
import { toast } from "sonner";

export interface AttendanceRecord {
  id: string;
  class_id: string;
  student_id: string;
  organization_id: string;
  date: string;
  status: "present" | "absent" | "late" | "excused";
  notes?: string;
  created_at: string;
  [key: string]: any; // Allow any other properties from the database
}

// Define a type for inserting new attendance records
interface AttendanceInsert {
  class_id: string;
  student_id: string;
  organization_id: string;
  date: string;
  status: "present" | "absent" | "late" | "excused";
  notes?: string;
}

export class AttendanceService {
  static async getAttendanceByClass(
    classId: string,
    date?: string
  ): Promise<AttendanceRecord[]> {
    try {
      let query = tables.class_attendance()
        .select("*")
        .eq("class_id", classId);

      // If date is provided, filter by date
      if (date) {
        query = query.eq("date", date);
      }

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });

      if (error) {
        console.error("Error fetching attendance:", error);
        toast.error("Failed to fetch attendance records");
        return [];
      }

      return data as AttendanceRecord[];
    } catch (error) {
      console.error("Error in getAttendanceByClass:", error);
      toast.error("An unexpected error occurred");
      return [];
    }
  }

  static async getStudentAttendance(
    studentId: string
  ): Promise<AttendanceRecord[]> {
    try {
      const { data, error } = await tables.class_attendance()
        .select("*, classes(*)")
        .eq("student_id", studentId)
        .order("date", { ascending: false });

      if (error) {
        console.error("Error fetching student attendance:", error);
        toast.error("Failed to fetch attendance records");
        return [];
      }

      return data as AttendanceRecord[];
    } catch (error) {
      console.error("Error in getStudentAttendance:", error);
      toast.error("An unexpected error occurred");
      return [];
    }
  }

  static async recordAttendance(
    attendanceData: AttendanceInsert
  ): Promise<AttendanceRecord | null> {
    try {
      // Check if attendance record already exists for this student on this date
      const { data: existingRecord, error: checkError } = await tables.class_attendance()
        .select("*")
        .eq("class_id", attendanceData.class_id)
        .eq("student_id", attendanceData.student_id)
        .eq("date", attendanceData.date)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking existing attendance:", checkError);
        toast.error("Failed to check existing attendance");
        return null;
      }

      let result;

      if (existingRecord) {
        // Update existing record
        const { data, error } = await tables.class_attendance()
          .update({ 
            status: attendanceData.status, 
            notes: attendanceData.notes 
          })
          .eq("id", existingRecord.id)
          .select()
          .single();

        if (error) {
          console.error("Error updating attendance:", error);
          toast.error("Failed to update attendance");
          return null;
        }

        result = data;
        toast.success("Attendance updated");
      } else {
        // Create new record with explicit typing
        const { data, error } = await tables.class_attendance()
          .insert({
            class_id: attendanceData.class_id,
            student_id: attendanceData.student_id,
            organization_id: attendanceData.organization_id,
            date: attendanceData.date,
            status: attendanceData.status,
            notes: attendanceData.notes
          })
          .select()
          .single();

        if (error) {
          console.error("Error recording attendance:", error);
          toast.error("Failed to record attendance");
          return null;
        }

        result = data;
        toast.success("Attendance recorded");
      }

      return result as AttendanceRecord;
    } catch (error) {
      console.error("Error in recordAttendance:", error);
      toast.error("An unexpected error occurred");
      return null;
    }
  }

  static async bulkRecordAttendance(
    records: AttendanceInsert[]
  ): Promise<boolean> {
    try {
      // Ensure we pass an array of properly structured objects
      const { error } = await tables.class_attendance().upsert(
        records,
        {
          onConflict: "class_id,student_id,date",
          ignoreDuplicates: false,
        }
      );

      if (error) {
        console.error("Error bulk recording attendance:", error);
        toast.error("Failed to record attendance");
        return false;
      }

      toast.success("Attendance recorded for all students");
      return true;
    } catch (error) {
      console.error("Error in bulkRecordAttendance:", error);
      toast.error("An unexpected error occurred");
      return false;
    }
  }

  static async getAttendanceStats(
    classId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    totalSessions: number;
    studentStats: Record<
      string,
      { present: number; absent: number; late: number; excused: number }
    >;
  }> {
    try {
      let query = tables.class_attendance()
        .select("*")
        .eq("class_id", classId);

      if (startDate) {
        query = query.gte("date", startDate);
      }

      if (endDate) {
        query = query.lte("date", endDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching attendance stats:", error);
        toast.error("Failed to fetch attendance statistics");
        return { totalSessions: 0, studentStats: {} };
      }

      // Ensure data is properly typed
      const records = data as AttendanceRecord[];

      // Calculate unique session dates
      const uniqueDates = new Set(records.map((record) => record.date));
      const totalSessions = uniqueDates.size;

      // Calculate stats per student
      const studentStats: Record<
        string,
        { present: number; absent: number; late: number; excused: number }
      > = {};

      records.forEach((record) => {
        if (!studentStats[record.student_id]) {
          studentStats[record.student_id] = {
            present: 0,
            absent: 0,
            late: 0,
            excused: 0,
          };
        }

        studentStats[record.student_id][record.status as 'present' | 'absent' | 'late' | 'excused']++;
      });

      return { totalSessions, studentStats };
    } catch (error) {
      console.error("Error in getAttendanceStats:", error);
      toast.error("An unexpected error occurred");
      return { totalSessions: 0, studentStats: {} };
    }
  }
}
